import React, { useContext } from "react";
import {
  B2BSpinner,
  TravelerSelectWorkflow,
  LoadingIndicator,
  TravelerSelectStep,
  useDeviceTypes,
  ToggleActions,
  VoidWindowNotice,
  hasInvalidBirthdate,
} from "halifax";
import {
  IPerson,
  PersonId,
  CallState,
  ADD_FREQUENT_FLYER,
  ITravelerStepErrors,
  CLICKED_ADD_TRAVELER,
  ClickedAddTravelerProperties,
  ICorpPerson,
} from "redmond";
import clsx from "clsx";
import { isEqual } from "lodash-es";
import dayjs from "dayjs";

import "./styles.scss";
import { FlightError } from "@b2bportal/air-booking-api";
import { getConfigTenant, isCorpTenant, isVesperTenant, useIsTravelApp } from "@capone/common";
import { FlightBookPassengerSelectionConnectorProps } from "./container";
import * as constants from "./textConstants";
import { ClientContext } from "../../../../App";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { errorRequiresTravelerUpdate } from "../../reducer/utils/getErrorTitles";
import { InfantSeatPickerModal } from "../InfantSeatPickerModal";
import { PassportAwarenessCard } from "../PassportAwarenessCard/component";
import {
  addTrackingProperties,
  useExperiments,
  XMOD_V1_EXPERIMENT,
  CORP_EMBEDDED_TRAVELER_FORM_EXPERIMENT,
} from "../../../../context/experiments";
import { config } from "../../../../api/config";
import {
  getIsLapInfant,
  useFlightBookPassengerSelect,
} from "./useFlightBookPassengerSelect";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";

export interface IFlightBookPassengerSelectionProps
  extends FlightBookPassengerSelectionConnectorProps {
  progress: TravelerSelectStep;
  setProgress: (progress: TravelerSelectStep) => void;
  onProgressChange?: (step: TravelerSelectStep) => void;
  onContinue?: (travelersChanged?: boolean) => void;
  onGoBack?: (travelersChanged?: boolean) => void;
  className?: string;
  selectionScreenHeaderElement?: JSX.Element;
  onReviewStep?: boolean;
  isMobile?: boolean;
  disabled?: boolean;
  combinedStep?: boolean;
  useLocalIds?: boolean;
  showErrors?: ITravelerStepErrors;
  setShowErrors?: (showErrors: ITravelerStepErrors) => void;
  saveButtonClicked?: boolean;
  localTravelerIds?: string[];
  setLocalTravelerIds?: (ids: string[]) => void;
  localLapInfantIds?: string[];
  setLocalLapInfantIds?: (ids: string[]) => void;
  showVoidWindowNotice?: boolean;
  showPassportAwareness?: boolean;
  hiddenTravelerIds: string[];
}

export const FlightBookPassengerSelection = (
  props: IFlightBookPassengerSelectionProps
) => {
  const {
    className,
    progress,
    setProgress,
    onProgressChange,
    onContinue,
    onGoBack,
    setUserSelectedPassengerIds,
    setUserSelectedLapInfantIds,
    updateUserPassenger,
    deleteUserPassenger,
    fetchUserPassengers,
    validatePassengers,
    userPassengerCallState,
    selectionScreenHeaderElement,
    onReviewStep,
    isMobile,
    disabled,
    combinedStep,
    useLocalIds,
    priceQuoteErrors,
    showErrors,
    setShowErrors,
    saveButtonClicked,
    setQuote,
    priceQuote,
    localLapInfantIds,
    setLocalLapInfantIds,
    localTravelerIds,
    setLocalTravelerIds,
    showVoidWindowNotice,
    showPassportAwareness,
    fetchCorpUserPassengers,
    passengerValidationCallState,
    userRoles,
  } = props;
  const isVesper = isVesperTenant(getConfigTenant());

  const { matchesMobile } = useDeviceTypes();

  const expState = useExperiments();

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const { sessionInfo } = useContext(ClientContext);

  const { isTravelApp } = useIsTravelApp();
  const isCorporate = isCorpTenant(config.TENANT);

  const useEmbeddedTravelerForm =
    isCorporate &&
    getExperimentVariant(
      expState.experiments,
      CORP_EMBEDDED_TRAVELER_FORM_EXPERIMENT
    ) === AVAILABLE;

  const handleValidationStart = React.useCallback(
    (travelerIds: string[]) => {
      if (isCorporate) {
        setValidatingTravelerIds(travelerIds);
      }
    },
    [isCorporate]
  );

  const {
    selectedTravelerIds,
    selectedLapInfantIds,
    travelers,
    tripFinalFlightDate,
    hasSelectedAdult,
    viewedTripSummaryProperties,
    currentInfantToSelectSeat,
    setCurrentInfantToSelectSeat,
    handleSelectedTravelerIds,
  } = useFlightBookPassengerSelect(
    isCorporate ? validatePassengers : undefined,
    handleValidationStart
  );

  const travelersChanged =
    !isEqual(selectedTravelerIds, localTravelerIds) ||
    !isEqual(selectedLapInfantIds, localLapInfantIds);

  const hasSelections =
    selectedTravelerIds.length + selectedLapInfantIds.length > 0;
  const errorMessage =
    hasSelections && !hasSelectedAdult
      ? constants.ADULT_REQUIRED_ERROR_MESSAGE
      : constants.ADD_TRAVELER_ERROR_MESSAGE;

  const minNameLength = 2;
  const [travelerErrorIds, setTravelerErrorIds] = React.useState<Set<string>>(
    new Set()
  );

  const currentErrorRequiresTravelerUpdate = React.useMemo(() => {
    if (priceQuoteErrors.length === 0) return false;
    return priceQuoteErrors.some(errorRequiresTravelerUpdate);
  }, [priceQuoteErrors]);

  // Track which passport errors have been received from the backend
  // We need to persist these even after priceQuoteErrors gets cleared
  const [receivedPassportErrors, setReceivedPassportErrors] = React.useState<{
    expired: boolean;
    expiring90Days: boolean;
    expiring180Days: boolean;
  }>({
    expired: false,
    expiring90Days: false,
    expiring180Days: false,
  });

  // Update tracked errors when priceQuoteErrors changes
  React.useEffect(() => {
    const hasExpiredPassportError = priceQuoteErrors.includes(
      FlightError.ExpiredPassport
    );
    const hasExpiring90DaysError = priceQuoteErrors.includes(
      FlightError.ExpiringPassport90Days
    );
    const hasExpiring180DaysError = priceQuoteErrors.includes(
      FlightError.ExpiringPassport180Days
    );

    // Once we receive an error, keep it tracked until travelers are fixed
    setReceivedPassportErrors((prev) => ({
      expired: prev.expired || hasExpiredPassportError,
      expiring90Days: prev.expiring90Days || hasExpiring90DaysError,
      expiring180Days: prev.expiring180Days || hasExpiring180DaysError,
    }));
  }, [priceQuoteErrors]);

  // Helper function to validate travelers and return IDs of those with errors
  const validateTravelersForErrors = React.useCallback(
    (travelerIds: string[]): Set<string> => {
      const errorIds = new Set<string>();

      travelerIds.forEach((travelerId) => {
        const traveler = travelers.find((t) => t.id === travelerId);
        if (traveler) {
          // Check for name length < 2 characters
          const hasShortName =
            (traveler.givenName && traveler.givenName.length < minNameLength) ||
            (traveler.surname && traveler.surname.length < minNameLength);

          let hasPassportError = false;

          if (traveler.passport?.expiration) {
            const passportExpiration = dayjs(
              traveler.passport.expiration,
              "YYYY-MM-DD",
              true
            );

            if (passportExpiration.isValid()) {
              const today = dayjs();
              const tripDate = dayjs(tripFinalFlightDate);

              // Only show passport errors if backend explicitly flagged them
              if (
                receivedPassportErrors.expired &&
                passportExpiration.isBefore(today)
              ) {
                hasPassportError = true;
              } else if (
                receivedPassportErrors.expiring90Days &&
                passportExpiration.isBefore(tripDate.add(90, "days"))
              ) {
                hasPassportError = true;
              } else if (
                receivedPassportErrors.expiring180Days &&
                passportExpiration.isBefore(tripDate.add(180, "days"))
              ) {
                hasPassportError = true;
              }
            }
          }

          if (hasShortName || hasPassportError) {
            errorIds.add(travelerId);
          }
        }
      });

      return errorIds;
    },
    [travelers, tripFinalFlightDate, receivedPassportErrors]
  );

  // Set initial errors when backend error occurs
  React.useEffect(() => {
    if (currentErrorRequiresTravelerUpdate) {
      const errorIds = validateTravelersForErrors([
        ...selectedTravelerIds,
        ...selectedLapInfantIds,
      ]);

      setTravelerErrorIds(errorIds);
    }
  }, [
    currentErrorRequiresTravelerUpdate,
    selectedTravelerIds,
    selectedLapInfantIds,
    validateTravelersForErrors,
  ]);

  // Re-validate travelers with errors (clear errors when fixed)
  React.useEffect(() => {
    if (travelerErrorIds.size > 0) {
      const stillHaveErrors = validateTravelersForErrors(
        Array.from(travelerErrorIds)
      );

      if (
        !isEqual(
          Array.from(stillHaveErrors).sort(),
          Array.from(travelerErrorIds).sort()
        )
      ) {
        setTravelerErrorIds(stillHaveErrors);
      }
    }
  }, [travelers, travelerErrorIds, validateTravelersForErrors]);

  // Clear tracked passport errors when all errors are resolved
  React.useEffect(() => {
    if (travelerErrorIds.size === 0 && !currentErrorRequiresTravelerUpdate) {
      setReceivedPassportErrors({
        expired: false,
        expiring90Days: false,
        expiring180Days: false,
      });
    }
  }, [travelerErrorIds, currentErrorRequiresTravelerUpdate]);

  const [validatingTravelerIds, setValidatingTravelerIds] = React.useState<
    string[]
  >([]);

  const isValidationInProgress =
    isCorporate && passengerValidationCallState === CallState.InProcess;
  const isDisabled = disabled || isValidationInProgress;

  React.useEffect(() => {
    if (
      isCorporate &&
      (passengerValidationCallState === CallState.Success ||
        passengerValidationCallState === CallState.Failed)
    ) {
      setValidatingTravelerIds([]);
    }
  }, [passengerValidationCallState, isCorporate]);

  React.useEffect(() => {
    isCorpTenant(config.TENANT)
      ? fetchCorpUserPassengers()
      : fetchUserPassengers();
  }, []);

  React.useEffect(() => {
    if (
      travelers.length === 1 &&
      !(travelers[0] as ICorpPerson).isMissingInfo &&
      !hasInvalidBirthdate(travelers[0].dateOfBirth)
    ) {
      const travelerIds = [travelers[0].id];
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: travelerIds,
        multiTicketType: viewedTripSummaryProperties?.multi_ticket_type,
      });
      validatePassengers(travelerIds, []);
    }
  }, [travelers]);

  // note: reset lap infant ids when the FE gets LapInfantsUnsupported
  React.useEffect(() => {
    if (
      priceQuoteErrors.find(
        (error) => error === FlightError.LapInfantsUnsupported
      )
    ) {
      setLocalLapInfantIds && setLocalLapInfantIds([]);
      setUserSelectedLapInfantIds({
        userSelectedLapInfantIds: [],
      });
    }
  }, [priceQuoteErrors]);

  const handleContinue = () => {
    if (hasSelections && !hasSelectedAdult) {
      setShowErrors?.({ ...showErrors, travelerSelect: true });
      return;
    }
    if (useLocalIds && travelersChanged) {
      setLocalTravelerIds && setLocalTravelerIds(selectedTravelerIds);
      setLocalLapInfantIds && setLocalLapInfantIds(selectedLapInfantIds);
    }
    onContinue && onContinue(travelersChanged);
  };

  const handleGoBack = () => {
    if (useLocalIds && travelersChanged) {
      setLocalTravelerIds && setLocalTravelerIds(selectedTravelerIds);
      setLocalLapInfantIds && setLocalLapInfantIds(selectedLapInfantIds);
    }
    onGoBack && onGoBack(travelersChanged);
  };

  React.useEffect(() => {
    // note: need to reset PQ or else when changing pax from review screen, price breakdown will not update as it will use the last PQ ran
    if (travelersChanged && priceQuote) {
      setQuote(null);
    }
  }, [travelersChanged]);

  return (
    <>
      {userPassengerCallState === CallState.InProcess ? (
        <LoadingIndicator
          className="flight-book-passenger-selection-loading-indicator"
          indicatorSize="small"
          indicator={B2BSpinner}
          message={
            userPassengerCallState === CallState.InProcess
              ? constants.UPDATE_TEXT
              : constants.VALIDATE_TEXT
          }
        />
      ) : (
        <TravelerSelectWorkflow
          showAdditionalInfoSection
          showFrequentFlyerSection
          showGenderField
          showNationalityField
          className={clsx("flight-book-passenger-selection-root", className, {
            "combined-step": combinedStep,
            redesigned: isVesper,
          })}
          travelers={travelers}
          progress={progress}
          setProgress={setProgress}
          userInfo={sessionInfo?.userInfo}
          disabled={isDisabled}
          hideMobileContinueButton={isValidationInProgress}
          isValidatingPassenger={
            isCorporate
              ? (travelerId: string) =>
                  validatingTravelerIds.includes(travelerId)
              : undefined
          }
          titles={{
            travelerInfoTitle: matchesMobile
              ? onReviewStep
                ? constants.TRAVELER_INFO_TEXT
                : constants.ADD_TRAVELERS_TEXT
              : constants.TRAVELER_INFO_TITLE_UPDATED,
            travelerInfoSubtitle: constants.TRAVELER_INFO_SUBTITLE,
            frequentFlyerTitle: constants.FREQUENT_FLYER_TITLE,
            additionalInfoTitle: constants.ADDITIONAL_INFO_TITLE,
            adultTitle: constants.ADULT_TITLE,
            childTitle: constants.CHILD_TITLE,
            infantSeatTitle: constants.INFANT_SEAT_TITLE,
            infantLapTitle: constants.INFANT_LAP_TITLE,
            addTravelers: constants.ADD_TRAVELERS_TEXT_UPDATED,
            editTravelerTitle: constants.EDIT_TRAVELER_TEXT,
            travelerInfoFormSubtitle: constants.ADD_TRAVELERS_SUBTITLE,
            travelerInfoSectionTitle: constants.TRAVELER_INFO_TEXT,
            passportTitle: constants.PASSPORT_TITLE,
            passportSubtitle: constants.PASSPORT_SUBTITLE,
            passportSubtitle2: constants.PASSPORT_SUBTITLE_2,
            completeTravelerProfileTitle: constants.COMPLETE_PROFILE_TITLE,
            completeTravelerProfileSubtitle:
              constants.COMPLETE_PROFILE_SUBTITLE,
          }}
          selectedTravelerIds={[
            ...selectedTravelerIds,
            ...selectedLapInfantIds,
          ]}
          setSelectedTravelerIds={handleSelectedTravelerIds}
          handleUpdatePassenger={(
            traveler: IPerson,
            hideProfileAction: ToggleActions,
            entryPoint?: string,
            updatePassport?: boolean,
            isNewTraveler?: boolean
          ) => {
            const freqFlyerKeys = Object.keys(traveler.frequentFlyer);
            if (
              freqFlyerKeys.length > 0 &&
              traveler.frequentFlyer !==
                travelers.find((t) => t.id === traveler.id)?.frequentFlyer
            ) {
              trackEvent({
                eventName: ADD_FREQUENT_FLYER,
                properties: {
                  frequent_flyer_program:
                    freqFlyerKeys.length > 0
                      ? freqFlyerKeys[freqFlyerKeys.length - 1]
                      : "",
                },
              });
            }
            if (getIsLapInfant(traveler, tripFinalFlightDate)) {
              updateUserPassenger(
                { person: traveler },
                false,
                updatePassport,
                undefined,
                isNewTraveler,
                undefined
              );
              setCurrentInfantToSelectSeat(traveler.id);
            } else {
              updateUserPassenger(
                {
                  person: traveler,
                  hideProfileAction,
                },
                true,
                updatePassport,
                undefined,
                isNewTraveler,
                undefined,
                entryPoint
              );
            }
          }}
          handleDeletePassenger={(travelerId: PersonId) =>
            deleteUserPassenger({ personId: travelerId })
          }
          isMobile={matchesMobile}
          onProgressChange={onProgressChange}
          onContinue={handleContinue}
          onGoBack={handleGoBack}
          selectionScreenHeaderElement={selectionScreenHeaderElement}
          buttonClassName="b2b"
          wrapButton={isMobile && !onReviewStep}
          showErrors={showErrors}
          setShowErrors={setShowErrors}
          saveButtonClicked={saveButtonClicked}
          hasSelectedAdult={hasSelectedAdult}
          errorMessage={errorMessage}
          isFlights
          bottomContent={
            showVoidWindowNotice || showPassportAwareness ? (
              <>
                {showVoidWindowNotice && <VoidWindowNotice />}
                {showPassportAwareness && <PassportAwarenessCard />}
              </>
            ) : undefined
          }
          showPassportSection
          requireNationality
          onClickAddNewTraveler={() => {
            trackEvent({
              eventName: CLICKED_ADD_TRAVELER,
              properties: addTrackingProperties(expState.trackingProperties, {
                entry_type: "checkout",
              } as ClickedAddTravelerProperties),
            });
          }}
          tenant={config.TENANT}
          trackEvent={trackEvent}
          checkMissingInfoCompletion={isCorpTenant(config.TENANT)}
          hideBackButton={isTravelApp}
          useXmodHeadings={isXmodRedesign}
          travelerErrorIds={travelerErrorIds}
          minNameLength={minNameLength}
          useEmbeddedTravelerForm={useEmbeddedTravelerForm}
          userRoles={userRoles}
        />
      )}
      <InfantSeatPickerModal
        currentInfantToSelectSeat={currentInfantToSelectSeat}
        setCurrentInfantToSelectSeat={setCurrentInfantToSelectSeat}
        userSelectedPassengerIds={selectedTravelerIds}
        userSelectedLapInfantIds={selectedLapInfantIds}
        setUserSelectedPassengerIds={(ids: string[]) => {
          setUserSelectedPassengerIds({
            userSelectedPassengerIds: ids,
            multiTicketType: viewedTripSummaryProperties?.multi_ticket_type,
          });
        }}
        setUserSelectedLapInfantIds={(ids: string[]) => {
          setUserSelectedLapInfantIds({
            userSelectedLapInfantIds: ids,
          });
        }}
      />
    </>
  );
};
